import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { templateForGridAndLevel } from "../../admin/story/adminSlidingUi";
import SlidingPlayMain from "../../admin/story/SlidingPlayMain";
import AdminSlidingService from "../../admin/story/AdminSlidingService";
import {
    getSlidingStoryTitle,
    pickSlidingCardImageUrl,
    pickSlidingPlayImageUrl,
} from "../browse-sliding/slidingBrowseUtils";
import SlidingService, { normalizeHomeSlidingGroup } from "../puzzles/sliding/SlidingService";
import {
    FEATURE_GRAPHIC_H,
    FEATURE_GRAPHIC_W,
    SLIDING_MASTER_FEATURE_GRID_SIZE,
    SLIDING_MASTER_FEATURE_LEVEL,
    SLIDING_MASTER_FEATURE_STORY_ID,
} from "./adSlidingMasterFeatureGraphic.config";
import "./ADSlidingMasterFeatureGraphic.css";

function pickStoryImageFromGroup(group) {
    if (!group || typeof group !== "object") return null;

    const story = group.story;
    const fromStory = pickSlidingPlayImageUrl(story) || pickSlidingCardImageUrl(story);
    if (fromStory) return fromStory;

    for (const template of group.templates ?? []) {
        const nested = template?.story;
        const fromNested =
            pickSlidingPlayImageUrl(nested) || pickSlidingCardImageUrl(nested);
        if (fromNested) return fromNested;
    }

    return null;
}

function pickFeatureGraphicPuzzle(group) {
    if (!group || typeof group !== "object") return null;

    const templates = Array.isArray(group.templates) ? group.templates : [];
    const tpl = templateForGridAndLevel(
        templates,
        SLIDING_MASTER_FEATURE_GRID_SIZE,
        SLIDING_MASTER_FEATURE_LEVEL,
    );
    if (!tpl) return null;

    const gridSize = SLIDING_MASTER_FEATURE_GRID_SIZE;
    const n = gridSize * gridSize;
    const layout = tpl.startingLayout ?? tpl.starting_layout;
    if (!Array.isArray(layout) || layout.length !== n) return null;

    const imageUrl = pickStoryImageFromGroup(group);
    if (!imageUrl) return null;

    return {
        imageUrl,
        gridSize,
        startingLayout: layout.map((x) => Number(x)),
        level: SLIDING_MASTER_FEATURE_LEVEL,
    };
}

function parseStoryIdInput(raw) {
    const trimmed = String(raw ?? "").trim();
    if (!trimmed) return "";
    const match = trimmed.match(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
    );
    return match ? match[0] : trimmed;
}

async function loadSlidingStoryGroup(storyId) {
    const admin = await AdminSlidingService.getGroupByStoryId(storyId);
    if (admin.success && admin.data) {
        return normalizeHomeSlidingGroup(admin.data) ?? admin.data;
    }
    return SlidingService.getStoryGroup(storyId);
}

export default function ADSlidingMasterFeatureGraphicPage() {
    const [storyInput, setStoryInput] = useState(
        `sliding/${SLIDING_MASTER_FEATURE_STORY_ID}`,
    );
    const [activeStoryId, setActiveStoryId] = useState(null);
    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [inputError, setInputError] = useState(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [previewLayout, setPreviewLayout] = useState({
        width: FEATURE_GRAPHIC_W,
        height: FEATURE_GRAPHIC_H,
    });
    const controlsRef = useRef(null);
    const previewRef = useRef(null);

    const handleSubmit = useCallback(async (event) => {
        event?.preventDefault?.();
        const storyId = parseStoryIdInput(storyInput);
        if (!storyId) {
            setInputError("Enter a story id or sliding/{storyId} path.");
            return;
        }

        setInputError(null);
        setError(null);
        setLoading(true);
        setActiveStoryId(storyId);
        setGroup(null);

        try {
            const data = await loadSlidingStoryGroup(storyId);
            if (!data) {
                setError("Story not found or not available.");
                return;
            }
            setGroup(data);
        } catch (e) {
            setError(e?.message || "Failed to load sliding puzzle.");
        } finally {
            setLoading(false);
        }
    }, [storyInput]);

    const content = useMemo(() => {
        if (!group) return null;
        const puzzle = pickFeatureGraphicPuzzle(group);
        return {
            storyTitle: getSlidingStoryTitle(group.story),
            puzzle,
        };
    }, [group]);

    useEffect(() => {
        const updateScale = () => {
            const controlsHeight = controlsRef.current?.offsetHeight ?? 0;
            const previewHeight = previewRef.current?.offsetHeight ?? FEATURE_GRAPHIC_H;
            const previewWidth = previewRef.current?.offsetWidth ?? FEATURE_GRAPHIC_W;
            const padding = 24;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - controlsHeight - padding;
            const scale = Math.min(1, availableW / previewWidth, availableH / previewHeight);
            setPreviewScale(scale);
            setPreviewLayout({
                width: Math.round(previewWidth * scale),
                height: Math.round(previewHeight * scale),
            });
        };

        const scheduleUpdate = () => window.requestAnimationFrame(updateScale);

        scheduleUpdate();
        window.addEventListener("resize", scheduleUpdate);

        const resizeObserver = new ResizeObserver(scheduleUpdate);
        if (controlsRef.current) resizeObserver.observe(controlsRef.current);
        if (previewRef.current) resizeObserver.observe(previewRef.current);

        return () => {
            window.removeEventListener("resize", scheduleUpdate);
            resizeObserver.disconnect();
        };
    }, [loading, error, activeStoryId, group]);

    useEffect(() => {
        const previousHtmlOverflow = document.documentElement.style.overflow;
        const previousBodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = previousHtmlOverflow;
            document.body.style.overflow = previousBodyOverflow;
        };
    }, []);

    return (
        <Box className="ad-sliding-master-feature-root">
            <Box ref={controlsRef} className="ad-sliding-master-feature-controls">
                <Typography className="ad-sliding-master-feature-caption" component="p">
                    Google Sliding Puzzle Master — Feature Graphic ({FEATURE_GRAPHIC_W}×
                    {FEATURE_GRAPHIC_H})
                </Typography>

                <Box
                    component="form"
                    className="ad-sliding-master-feature-form"
                    onSubmit={handleSubmit}
                >
                    <TextField
                        label="Story"
                        placeholder="sliding/a5364e08-d60d-4aee-8e00-9e952b0221d2"
                        value={storyInput}
                        onChange={(e) => {
                            setStoryInput(e.target.value);
                            if (inputError) setInputError(null);
                        }}
                        size="small"
                        fullWidth
                        required
                        error={Boolean(inputError)}
                        helperText={
                            inputError ||
                            `${SLIDING_MASTER_FEATURE_GRID_SIZE}×${SLIDING_MASTER_FEATURE_GRID_SIZE} ${SLIDING_MASTER_FEATURE_LEVEL} puzzle in 1024×500 frame`
                        }
                        sx={{ maxWidth: 560 }}
                    />
                    <Button type="submit" variant="contained" disabled={loading}>
                        {loading ? "Loading…" : "Submit"}
                    </Button>
                </Box>
            </Box>

            <Box className="ad-sliding-master-feature-stage">
                <Box
                    className="ad-sliding-master-feature-fit"
                    style={{
                        width: previewLayout.width,
                        height: previewLayout.height,
                    }}
                >
                    <Box
                        ref={previewRef}
                        className="ad-sliding-master-feature-scaler"
                        style={{
                            transform: `scale(${previewScale})`,
                            width: FEATURE_GRAPHIC_W,
                        }}
                    >
                    <Box
                        className="ad-sliding-master-feature-frame"
                        sx={{ width: FEATURE_GRAPHIC_W, height: FEATURE_GRAPHIC_H }}
                    >
                        {loading ? (
                            <Box className="ad-sliding-master-feature-center">
                                <CircularProgress size={36} sx={{ color: "#3BA666" }} />
                            </Box>
                        ) : error ? (
                            <Box className="ad-sliding-master-feature-center">
                                <Typography color="error" align="center" sx={{ px: 2 }}>
                                    {error}
                                </Typography>
                            </Box>
                        ) : !activeStoryId ? (
                            <Box className="ad-sliding-master-feature-center">
                                <Typography color="text.secondary" align="center" sx={{ px: 2 }}>
                                    Enter a story and click Submit.
                                </Typography>
                            </Box>
                        ) : !content?.puzzle ? (
                            <Box className="ad-sliding-master-feature-center">
                                <Typography color="text.secondary" align="center" sx={{ px: 2 }}>
                                    {`${SLIDING_MASTER_FEATURE_GRID_SIZE}×${SLIDING_MASTER_FEATURE_GRID_SIZE} ${SLIDING_MASTER_FEATURE_LEVEL} template or story image missing.`}
                                </Typography>
                            </Box>
                        ) : (
                            <Box className="ad-sliding-master-feature-puzzle-wrap">
                                <Box className="ad-sliding-master-feature-puzzle-board">
                                    <SlidingPlayMain
                                        imageUrl={content.puzzle.imageUrl}
                                        gridSize={content.puzzle.gridSize}
                                        startingLayout={content.puzzle.startingLayout}
                                        hideCompletedCaption
                                        fillParent
                                        playMoveSound={false}
                                        gridGutterColor="#ffffff"
                                        gridBorderColor="#3ba666"
                                    />
                                </Box>
                            </Box>
                        )}

                        <Typography className="ad-sliding-master-feature-size-label" component="span">
                            {FEATURE_GRAPHIC_W} × {FEATURE_GRAPHIC_H}
                        </Typography>
                    </Box>

                    {!loading && content?.storyTitle ? (
                        <Typography className="ad-sliding-master-feature-story" component="p">
                            {content.storyTitle}
                        </Typography>
                    ) : null}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
